import DOMPurify from 'dompurify'
import React, { createContext, useContext } from 'react'
import { createPortal } from 'react-dom'

import {
  GOV_SANDBOX_ENVIRONMENT,
  ADNOC_STAGING_ENVIRONMENT,
  GOV_PROD_ENVIRONMENT,
  GOV_STAGING_ENVIRONMENT,
  UNNPI_PROD_ENVIRONMENT,
  UNNPI_STAGING_ENVIRONMENT,
} from './defines'

/**
 * Variables used to configure this app. Note that "runtimeVariables" is added
 * via Typescript module augmentation like so:
 * <script language="javascript">
 *    declare module 'app-kit' {
 *     interface GeckoAppConfig {
 *       runtimeVariables: typeof runtimeVariables
 *     }
 *   }
 * </script>
 */
export interface GeckoAppConfig {
  appName: string
  // "runtimeVariables" will be added here via Typescript module augmentation
}

/** Variables reflecting the environment this app is running in */
export type EnvironmentVariables = {
  IS_PROD: boolean
  IS_DEV: boolean
  IS_LOCAL: boolean
  IS_GOV_ENVIRONMENT: boolean
  IS_ADNOC_ENVIRONMENT: boolean
  IS_MULTI_TENANT_ENVIRONMENT: boolean
}

/** Variables only available at runtime which are defined by this specific app */
export type RuntimeVariables = GeckoAppConfig['runtimeVariables']

// DO NOT export this, instead encourage the use of hooks
const GeckoAppKitContext = createContext<
  | {
      environmentVariables: EnvironmentVariables
      runtimeVariables: RuntimeVariables
    }
  | undefined
>(undefined)

/** A hook accessing this app's current environmental variables */
export const useEnvironmentVariables = (): EnvironmentVariables => {
  const context = useContext(GeckoAppKitContext)
  if (context === undefined)
    throw new Error('useEnvironmentVariables() must be used within GeckoAppKit')
  return context.environmentVariables
}

/**
 * A hook accessing this app's current "custom" runtime variables. Note that
 * these are accessed via Typescript module augmentation like so:
 * <script language="javascript">
 *    declare module 'app-kit' {
 *     interface GeckoAppConfig {
 *       runtimeVariables: typeof runtimeVariables
 *     }
 *   }
 * </script>
 */
export const useRuntimeVariables = (): RuntimeVariables => {
  const context = useContext(GeckoAppKitContext)
  if (context === undefined)
    throw new Error('useRuntimeVariables() must be used within GeckoAppKit')
  return context.runtimeVariables
}

/**
 * A high-level provider for a basic Gecko React app.
 * @param config        A blob of data for configuring this application,
 *                      including its environmental (required) and custom
 *                      runtime variables
 * @param children      The app itself, nested within this provider
 * @constructor
 */
export const GeckoAppKit = ({
  children,
  config,
}: {
  children: React.ReactNode
  config: GeckoAppConfig
}) => {
  if (!config) throw new Error('config is required')

  const { ENVIRONMENT, BASE_PATH, RUNNING_ON } = config.runtimeVariables

  const isGovEnvironment = [
    GOV_SANDBOX_ENVIRONMENT,
    GOV_STAGING_ENVIRONMENT,
    GOV_PROD_ENVIRONMENT,
    UNNPI_STAGING_ENVIRONMENT,
    UNNPI_PROD_ENVIRONMENT,
  ].includes(ENVIRONMENT)

  const environmentVariables: EnvironmentVariables = {
    IS_PROD: ENVIRONMENT === 'prod',
    IS_DEV: ENVIRONMENT === 'dev',
    IS_LOCAL: ENVIRONMENT === 'local',
    IS_GOV_ENVIRONMENT: isGovEnvironment,
    IS_ADNOC_ENVIRONMENT: ENVIRONMENT === ADNOC_STAGING_ENVIRONMENT,
    IS_MULTI_TENANT_ENVIRONMENT: !isGovEnvironment && RUNNING_ON !== '2f',
  }

  const sanitizedBasePath = DOMPurify.sanitize(BASE_PATH)
  const faviconUrl = environmentVariables.IS_LOCAL
    ? `${sanitizedBasePath}/local-favicon.svg`
    : environmentVariables.IS_DEV
      ? `${sanitizedBasePath}/dev-favicon.svg`
      : `${sanitizedBasePath}/prod-favicon.svg`

  return (
    <GeckoAppKitContext.Provider
      value={{
        environmentVariables: environmentVariables,
        runtimeVariables: config.runtimeVariables,
      }}>
      {createPortal(
        <link id='link' rel='icon' type='image/svg+xml' href={faviconUrl} />,
        document.head
      )}
      {children}
    </GeckoAppKitContext.Provider>
  )
}
